<template>
  <section id="politirapport-generator" class="content white large-padding">
    <form class="hide-for-print" @submit.prevent="handleSubmit" spellcheck="false">
      <TheLoader :active="loading || exporting" :update="true"/>

      <fieldset style="margin: 0;">
        <div class="grid-x grid-padding-x">
          <TheIntegerInput v-model="form.maxPrice" containerClassName="cell shrink" label="Lav vurdering, f.o.m" name="maxPrice"/>

          <TheAutoCompleteInput v-model.trim="form.code"
            ref="auksjon_input"
            containerClassName="cell large-2"
            name="Auksjon"
            :placeholder="'Velg auksjon'"
            :label="'Tilhørende auksjon'"
            :onInputCallback="onAuksjonSearchInput"
            :onSelectCallback="onAuksjonSearchSelected"
            :disabled="isDisabledCode"
            :debounce="750"
            :minquery="2"
          >
            <div slot="item" slot-scope="props">
              <strong>{{props.item.Auksjon}}</strong>, {{props.item.Beskrivelse}}
              <span v-if="props.item.Auksjon_dato">
                ({{ props.item.Auksjon_dato | avantidate }})
              </span>
            </div>
          </TheAutoCompleteInput>

          <TheSelectInput v-model="form.avdelingsid" containerClassName="cell large-2" label="Avdeling" name="avdelingsid" :items="avdDropdown" :disabled="isDisabledDate"/>

          <div class="cell auto">
            <label for="from">Registrert dato, f.o.m: *</label>
            <div class="input-container">
              <b-datepicker
                @input="(date) => formatDate(date, 'from')"
                :show-week-number="false"
                :first-day-of-week="1"
                :editable="false"
                :disabled="isDisabledDate"
                v-model="form.date.from"
                name="from"
                placeholder="Vis kalender..."
                icon="calendar-today"
                ref="from"
                id="from">
                <button class="button disabled" v-if="!form.date.from" @click.prevent="$refs.from.toggle()"><span>Slett dato</span></button>
                <button class="button" v-else @click.prevent="form.date.from = null"><span>Slett dato</span></button>
              </b-datepicker>
              <span class="glyph">
                <svg viewBox="0 0 44 44"><use xlink:href="/static/sprites.svg#glyph-input-date"></use></svg>
              </span>
            </div>
          </div>
          <div class="cell auto">
            <label for="till">Registrert dato, t.o.m: *</label>
            <div class="input-container">
              <b-datepicker
                @input="(date) => formatDate(date, 'till')"
                :show-week-number="false"
                :first-day-of-week="1"
                :editable="false"
                :disabled="isDisabledDate"
                v-model="form.date.till"
                name="till"
                placeholder="Vis kalender..."
                icon="calendar-today"
                ref="till"
                id="till">
                <button class="button disabled" v-if="!form.date.till" @click.prevent="$refs.till.toggle()"><span>Slett dato</span></button>
                <button class="button" v-else @click.prevent="form.date.till = null"><span>Slett dato</span></button>
              </b-datepicker>
              <span class="glyph">
                <svg viewBox="0 0 44 44"><use xlink:href="/static/sprites.svg#glyph-input-date"></use></svg>
              </span>
            </div>
          </div>

          <div class="cell auto">
            <label>&nbsp;</label>
            <template v-if="isValidForSubmit">
              <a v-if="!(loading || exporting)" href="javascript:;" @click="handleSubmit()" class="button primary submit tiny">Ok</a>
              <a v-else-if="loading" href="javascript:;" class="button primary submit tiny loading">Ok <span class="loader-spinner"></span></a>
              <a v-else href="javascript:;" class="button primary submit tiny" disabled>Ok</a>

              <template v-if="!found || !found.objects || found.objects.length < 1">
                <a href="javascript:;" class="button primary submit tiny" disabled style="margin-left: 0.25rem;">Exporter</a>
              </template>
              <template v-else-if="exporting">
                <a href="javascript:;" class="button primary submit tiny loading" style="margin-left: 0.25rem;">Exporter <span class="loader-spinner"></span></a>
              </template>
              <template v-else>
                <a @click="handleExport()" href="javascript:;" class="button primary submit tiny" style="margin-left: 0.25rem;">Exporter</a>
              </template>
            </template>
            <template v-else>
              <a href="javascript:;" class="button primary submit tiny" disabled>Ok</a>
            </template>
          </div>
        </div>
      </fieldset>
    </form>

    <div class="content-container flex-column" v-if="found">
      <div class="content-container flex-row content-x-scroll content-y-scroll table-results">
        <!-- Pagination -->
        <div class="pagination-loader-container sidebar-padding">
          <div class="pagination-loader">
            <span class="results" v-if="found.objects.length === 1">Fant <strong>{{found.objects.length | thousands}}</strong> objekt</span>
            <span class="results" v-else>Fant <strong>{{found.objects.length | thousands}}</strong> objekter</span>

            <a href="javascript:;" @click="scrollToTop()" title="Scroll til toppen" class="button tiny scrolltop"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-arrow-left"></use></svg></a>
          </div>
        </div>
        <!-- // Pagination -->

        <table class="large fixed compressed" style="height: 0">
          <thead>
            <tr>
              <th class="unsortable">Navn</th>
              <th class="unsortable">Firmanavn</th>
              <th class="unsortable">Adresse</th>
              <th class="unsortable">Postnummer</th>
              <th class="unsortable">Poststed</th>
              <th class="unsortable">Land</th>
              <th class="unsortable">Telefon</th>
              <th class="unsortable">Legitimasjon</th>
              <th class="unsortable">Kontraktinfo</th>
              <th class="unsortable">Kunstner</th>
              <th class="unsortable">Tittel</th>
              <th class="unsortable">Objektnr</th>
              <th class="unsortable">Objekttittel</th>
              <th class="unsortable">Hoyde</th>
              <th class="unsortable">Lengde</th>
              <th class="unsortable">Dybde</th>
              <th class="unsortable">Diameter</th>
              <th class="unsortable">Mottatt</th>
              <th class="unsortable">Salgspris</th>
              <th class="unsortable">Lav vurdering</th>
              <th class="unsortable">Avdeling</th>
              <th class="unsortable">Sistelokasjon</th>
              <th class="unsortable">Auksjon</th>
            </tr>
          </thead>
          <tbody class="empty" v-if="found.objects.length < 1">
            <tr><td colspan="18"><em>Fant ingen objekter...</em></td></tr>
          </tbody>
          <tbody v-else>
            <tr class="header" v-for="item in found.objects" :key="item.Objektid">
              <template v-if="item.innlevav">
                <td class="nowrap">{{ sellerName(item.innlevav) }}</td>
                <td class="nowrap">{{ sellerCompanyName(item.innlevav) }}</td>
                <td class="nowrap">{{ sellerAddress(item.innlevav) }}</td>
                <td class="nowrap">{{ sellerZip(item.innlevav) }}</td>
                <td class="nowrap">{{ sellerCity(item.innlevav) }}</td>
                <td class="nowrap">{{ sellerCountry(item.innlevav) }}</td>
                <td class="nowrap">{{ item.innlevav.full_phone }}</td>
              </template>
              <template v-else>
                <td colspan="7">-</td>
              </template>

              <td class="nowrap">{{ item.kontrakt && item.kontrakt.legitimasjon ? item.kontrakt.legitimasjon.Tekst : '-' }}</td>
              <td class="nowrap">{{ item.kontrakt && item.kontrakt.Kontraktinfo ? (item.kontrakt.Kontraktinfo || '-') : '-' }}</td>

              <td class="nowrap">{{ item.kunstner ? item.kunstner.Navn : (item.Kunstner || '-') }}</td>
              <td class="nowrap">{{ item.Katalogtekst1 }}</td>

              <td class="nowrap">{{ item.Objektnr }}</td>
              <td>{{ item.Objekt3 }}</td>

              <td v-if="item.Hoyde">{{ item.Hoyde }}<span v-if="item.Hoyde_Enhet">{{ item.Hoyde_Enhet }}</span></td>
              <td v-else>-</td>
              <td v-if="item.Lengde">{{ item.Lengde }}<span v-if="item.Lengde_Enhet">{{ item.Lengde_Enhet }}</span></td>
              <td v-else>-</td>
              <td v-if="item.Dybde">{{ item.Dybde }}<span v-if="item.Dybde_Enhet">{{ item.Dybde_Enhet }}</span></td>
              <td v-else>-</td>
              <td v-if="item.Diameter">{{ item.Diameter }}<span v-if="item.Diameter_Enhet">{{ item.Diameter_Enhet }}</span></td>
              <td v-else>-</td>

              <td>{{ item.Registrert_dato | avantidatetime }}</td>
              <td>{{ item.Salgspris | thousands }}</td>
              <td>{{ item.Vurderingspris2 | thousands }}</td>

              <td>{{ item.avdeling ? item.avdeling.Beskrivelse : '-' }}</td>
              <td>{{  (item.lokasjon ? (item.lokasjon.Lokasjonkode || '-') : (item.Sistelokasjon || '-')) }}</td>
              <td>{{ item.Auksjon }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </section>
</template>

<script>
import InstillingerService from '@/services/InstillingerService'
import ObjektService from '@/services/ObjektService'
import AuksjonService from '@/services/AuksjonService'
import TheAutoCompleteInput from '../../Inputs/TheAutoCompleteInput'
import TheLoader from '../../TheLoader.vue'
import moment from 'moment'
import Papa from 'papaparse'
import fileDownload from 'js-file-download'

export default {
  name: 'PolitirapportGenerator',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    TheAutoCompleteInput,
    TheLoader
  },
  data () {
    return {
      form: {
        code: null,
        date: {
          from: null,
          till: null
        },
        maxPrice: 20000,
        avdelingsid: null
      },
      avdDropdown: [],
      exporting: false,
      loading: false,
      found: null
    }
  },
  computed: {
    isDisabledCode: function () {
      return !!this.form.date.from ||
        !!this.form.date.till ||
        !(this.form.avdelingsid === null || this.form.avdelingsid === '' || this.form.avdelingsid === undefined)
    },
    isDisabledDate: function () {
      return !!this.form.code
    },
    isValidForSubmit: function () {
      return this.form.code || (this.form.date.from && this.form.date.till)
    },
    title: function () {
      return this.$route.meta.title
    }
  },
  created () {
    this.avdDropdown.unshift({key: this.form.Avdelingsid, val: 'Laster...'})

    InstillingerService('avdeling').dropdown()
      .then(response => {
        this.avdDropdown = response.data || []
        this.avdDropdown.unshift({key: null, val: 'Alle avdelinger'})
      })
  },
  methods: {
    scrollToTop: function () {
      window.jQuery('.table-results').scrollTop(0)
    },
    sellerName: function (seller) {
      return seller.full_name || '-'
    },
    sellerCompanyName: function (seller) {
      return seller.FirmaNavn || '-'
    },
    sellerAddress: function (seller) {
      if (!seller.kundeadresse) {
        return '-'
      }

      let retval = []
      let address = seller.kundeadresse.find(i => i.Type === 'faktura') || {}

      if (address.Co) {
        retval.push(address.Co)
      }

      if (address.Adresse) {
        retval.push(address.Adresse)
      }

      if (address.Adresse2) {
        retval.push(address.Adresse2)
      }

      return retval.length > 0 ? retval.join(', ') : '-'
    },
    sellerZip: function (seller) {
      if (!seller.kundeadresse) {
        return '-'
      }

      const address = seller.kundeadresse.find(i => i.Type === 'faktura') || {}

      return address.Postnr || '-'
    },
    sellerCity: function (seller) {
      if (!seller.kundeadresse) {
        return '-'
      }

      const address = seller.kundeadresse.find(i => i.Type === 'faktura') || {}

      return address.Poststed || '-'
    },
    sellerCountry: function (seller) {
      if (!seller.kundeadresse) {
        return '-'
      }

      const address = seller.kundeadresse.find(i => i.Type === 'faktura') || {}

      return address.land ? (address.land.Landbeskrivelse || '-') : '-'
    },
    formatDate: function (date, key) {
      if (this.form.date.hasOwnProperty(key)) {
        this.form.date[key] = new Date(moment(date).format('YYYY-MM-DD'))
      }
    },
    handleSubmit: function () {
      if (!this.isFormSubmittable() || !this.isValidForSubmit) {
        return
      }

      this.loading = true

      let post = {
        maxPrice: Number(this.form.maxPrice),
        avdelingsid: null
      }
      if (this.form.code) {
        post.code = this.form.code
      } else {
        post.date = {
          from: moment(this.form.date.from).format('YYYY-MM-DD'),
          till: moment(this.form.date.till).format('YYYY-MM-DD')
        }

        post.avdelingsid = this.form.avdelingsid
      }

      ObjektService.getPoliceReport(post)
        .then(response => {
          if (response.data.objects) {
            this.found = {
              objects: response.data.objects || []
            }
          } else {
            throw new Error('Kunne ikke finne objekter...')
          }
        })
        .catch(error => {
          console.log('Error', error)
          this.$helpers.showErrorAlert('Feil', 'Noe gikk galt, prøv igjen', 'danger')
        })
        .then(() => {
          this.loading = false
        })
    },
    onAuksjonSearchInput: function (query) {
      return AuksjonService.searchInSettings(query)
    },
    onAuksjonSearchSelected: function (item) {
      if (item) {
        this.form.code = item.Auksjon || null
      }
    },
    exportPolitirapport: function () {
      return new Promise((resolve, reject) => {
        const data = []

        const headers = [
          'Navn',
          'Firmanavn',
          'Adresse',
          'Postnummer',
          'Poststed',
          'Land',
          'Telefon',
          'Legitimasjon',
          'Kontraktinfo',
          'Kunstner',
          'Tittel',
          'Objektnr',
          'Objekttittel',
          'Hoyde',
          'Lengde',
          'Dybde',
          'Diameter',
          'Mottatt',
          'Salgspris',
          'Lav vurdering',
          'Avdeling',
          'Sistelokasjon',
          'Auksjon'
        ]

        data.push(headers)

        this.found.objects.forEach(item => {
          const row = []

          if (item.innlevav) {
            row.push(this.sellerName(item.innlevav))
            row.push(this.sellerCompanyName(item.innlevav))
            row.push(this.sellerAddress(item.innlevav))
            row.push(this.sellerZip(item.innlevav))
            row.push(this.sellerCity(item.innlevav))
            row.push(this.sellerCountry(item.innlevav))
            row.push(item.innlevav.full_phone)
          } else {
            row.push('-')
            row.push('-')
            row.push('-')
            row.push('-')
            row.push('-')
            row.push('-')
            row.push('-')
          }

          row.push(item.kontrakt && item.kontrakt.legitimasjon ? item.kontrakt.legitimasjon.Tekst : '-')
          row.push(item.kontrakt && item.kontrakt.Kontraktinfo ? (item.kontrakt.Kontraktinfo || '-') : '-')

          row.push(item.kunstner ? item.kunstner.Navn : (item.Kunstner || '-'))
          row.push(item.Katalogtekst1)
          row.push(item.Objektnr)
          row.push(item.Objekt3)
          row.push(item.Hoyde ? item.Hoyde + '' + item.Hoyde_Enhet : '-')
          row.push(item.Lengde ? item.Lengde + '' + item.Lengde_Enhet : '-')
          row.push(item.Dybde ? item.Dybde + '' + item.Dybde_Enhet : '-')
          row.push(item.Diameter ? item.Diameter + '' + item.Diameter_Enhet : '-')
          row.push(this.$options.filters.avantidatetime(item.Registrert_dato))
          row.push(this.$options.filters.thousands(item.Salgspris))
          row.push(this.$options.filters.thousands(item.Vurderingspris2))
          row.push(item.avdeling ? item.avdeling.Beskrivelse : '-')
          row.push((item.lokasjon ? (item.lokasjon.Lokasjonkode || '-') : (item.Sistelokasjon || '-')))
          row.push(item.Auksjon)

          data.push(row)
        })

        // Finally make csv and force download...
        const csv = '\ufeff' + Papa.unparse(data, { delimiter: ';' })
        const blob = new Blob([csv], {
          type: 'application/csvcharset=utf-8'
        })

        fileDownload(blob, 'politirapport-' + moment().format('DDMMYY.Hms') + '.csv')

        resolve()
      })
    },
    handleExport: function () {
      if (!this.found || !this.found.objects || this.found.objects.length < 1) {
        this.$helpers.showErrorAlert('Feil', 'Ingen objekter at skrive ut')
        return
      }

      this.exporting = true
      this.exportPolitirapport()
        .then(() => {
          this.exporting = false
        })
    }
  }
}
</script>

<style scoped>
  table.fixed tbody.empty tr td:after {
    background: none !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/Admin/Reports/PolitirapportGenerator.vue